import { Field } from './Field';
import { isObject } from 'src/utils/typeGuards';
import { objectKeysOfUnion } from 'src/utils/objectKeysOfUnion';
import { CriteriaValue } from '../../../types/AlertConfiguration';

namespace Operator {
  export type Logical = '&&' | '||';
  export type Equality = '==' | '!=';
  export type Comparison = '<' | '>' | '<=' | '>=';
  export type Operand = Field.Ref | Field.Value;

  /**
   * An example of Expression object:
   * ```
   * {
   *   "||": [
   *      { "==": [{ field: "rating" }, { field: "another_rating" }] },
   *      {
   *         "&&": [
   *            { ">=": [{ field: "rating" }, 3] },
   *            { "==": [5, { field: "another_rating" }] }
   *         ]
   *      }
   *   ]
   * }
   * ```
   */
  export type Expression =
    | { [K in Logical]: Expression[] } // See example
    | { [K in Equality | Comparison]: [Operand, Operand] };

  export function calculateExpression(expr: Expression, values: Partial<CriteriaValue>) {
    function getValue(operand: Operand) {
      if (isObject(operand) && 'field' in operand) {
        // TODO we should define which types we gonna use for values
        // `joining` is a temporal measure since `string` is preferrable now
        const result = values[operand.field];
        return Array.isArray(result) ? result.join() : result;
      }
      return operand;
    }

    function getNumericValue(operand: Operand): number {
      return Number(getValue(operand) ?? 0);
    }

    function calculate(expr: Expression): boolean {
      const [operator] = objectKeysOfUnion(expr);
      switch (operator) {
        case '&&':
          if (operator in expr) {
            return expr[operator].every(calculate);
          }
          break;
        case '||':
          if (operator in expr) {
            return expr[operator].some(calculate);
          }
          break;
        case '==':
          if (operator in expr) {
            const [left, right] = expr[operator];
            return getValue(left) === getValue(right);
          }
          break;
        case '!=':
          if (operator in expr) {
            const [left, right] = expr[operator];
            return getValue(left) !== getValue(right);
          }
          break;
        case '<':
          if (operator in expr) {
            const [left, right] = expr[operator];
            return getNumericValue(left) < getNumericValue(right);
          }
          break;
        case '>':
          if (operator in expr) {
            const [left, right] = expr[operator];
            return getNumericValue(left) > getNumericValue(right);
          }
          break;
        case '<=':
          if (operator in expr) {
            const [left, right] = expr[operator];
            return getNumericValue(left) <= getNumericValue(right);
          }
          break;
        case '>=':
          if (operator in expr) {
            const [left, right] = expr[operator];
            return getNumericValue(left) >= getNumericValue(right);
          }
          break;
      }
      throw Error(`the operator "${operator}" is not supported.`);
    }

    return calculate(expr);
  }
}

export { Operator };
